import React, { useState } from 'react';
import axios from 'axios';

function InputForm({ onSubmit, onSuccess, onError }) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [linkedinFiles, setLinkedinFiles] = useState([]); // Array for multiple files
  const [linkedinText, setLinkedinText] = useState('');
  const [inputMethod, setInputMethod] = useState('screenshot'); // 'screenshot', 'pdf', or 'text'
  const [previewUrls, setPreviewUrls] = useState([]); // Array for multiple previews

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!fullName.trim()) {
      onError('Please enter your full name');
      return;
    }

    if (!email) {
      onError('Please enter your email address');
      return;
    }

    if (!websiteUrl) {
      onError('Please enter your website URL');
      return;
    }

    if (!linkedinUrl) {
      onError('Please enter your LinkedIn profile URL');
      return;
    }

    if (inputMethod === 'screenshot' && linkedinFiles.length === 0) {
      onError('Please upload at least one screenshot');
      return;
    }

    if (inputMethod === 'pdf' && linkedinFiles.length === 0) {
      onError('Please upload your LinkedIn PDF');
      return;
    }

    if (inputMethod === 'text' && !linkedinText.trim()) {
      onError('Please paste your LinkedIn profile content');
      return;
    }

    onSubmit();

    try {
      const formData = new FormData();
      formData.append('fullName', fullName.trim());
      formData.append('email', email);
      formData.append('websiteUrl', websiteUrl);
      formData.append('linkedinUrl', linkedinUrl);
      formData.append('inputMethod', inputMethod);

      if (inputMethod === 'screenshot' && linkedinFiles.length > 0) {
        // Append multiple files
        linkedinFiles.forEach(file => {
          formData.append('linkedinFiles', file);
        });
      } else if (inputMethod === 'pdf' && linkedinFiles.length > 0) {
        formData.append('linkedinFiles', linkedinFiles[0]);
      } else if (inputMethod === 'text') {
        formData.append('linkedinText', linkedinText);
      }

      const response = await axios.post('/api/audit/generate', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      onSuccess(response.data.audit, response.data.leadId);
    } catch (err) {
      console.error('Submission error:', err);
      onError(err.response?.data?.error || 'Failed to generate audit. Please try again.');
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    if (inputMethod === 'screenshot') {
      const imageFiles = files.filter(file => file.type.startsWith('image/'));
      if (imageFiles.length !== files.length) {
        onError('Please upload only image files (PNG, JPG, etc.)');
        return;
      }
      // Add new files to existing array (up to 5 total)
      const newFiles = [...linkedinFiles, ...imageFiles].slice(0, 5);
      setLinkedinFiles(newFiles);
      // Create preview URLs
      const newUrls = newFiles.map(file => URL.createObjectURL(file));
      // Revoke old URLs to prevent memory leaks
      previewUrls.forEach(url => URL.revokeObjectURL(url));
      setPreviewUrls(newUrls);
    } else if (inputMethod === 'pdf') {
      const file = files[0];
      if (file.type === 'application/pdf') {
        setLinkedinFiles([file]);
        setPreviewUrls([]);
      } else {
        onError('Please upload a PDF file');
      }
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  };

  const removeFile = (index) => {
    const newFiles = linkedinFiles.filter((_, i) => i !== index);
    setLinkedinFiles(newFiles);
    // Revoke the removed URL and update array
    URL.revokeObjectURL(previewUrls[index]);
    const newUrls = previewUrls.filter((_, i) => i !== index);
    setPreviewUrls(newUrls);
  };

  const clearFiles = () => {
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    setLinkedinFiles([]);
    setPreviewUrls([]);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Full Name Input */}
      <div>
        <label className="block text-amber-400 font-semibold mb-2">
          Full Name *
        </label>
        <input
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          placeholder="John Smith"
          className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-amber-400 transition"
          required
        />
      </div>

      {/* Email Input */}
      <div>
        <label className="block text-amber-400 font-semibold mb-2">
          Email Address *
        </label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-amber-400 transition"
          required
        />
        <p className="text-slate-500 text-sm mt-1">We'll send your audit results here</p>
      </div>

      {/* Website URL */}
      <div>
        <label className="block text-amber-400 font-semibold mb-2">
          Website URL *
        </label>
        <input
          type="url"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          placeholder="https://yourwebsite.com"
          className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-amber-400 transition"
          required
        />
      </div>

      {/* LinkedIn Profile URL */}
      <div>
        <label className="block text-amber-400 font-semibold mb-2">
          LinkedIn Profile URL *
        </label>
        <input
          type="url"
          value={linkedinUrl}
          onChange={(e) => setLinkedinUrl(e.target.value)}
          placeholder="https://linkedin.com/in/yourprofile"
          className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-amber-400 transition"
          required
        />
        <p className="text-slate-500 text-sm mt-1">Your public LinkedIn profile link</p>
      </div>

      {/* LinkedIn Input Method Toggle */}
      <div>
        <label className="block text-amber-400 font-semibold mb-2">
          LinkedIn Profile *
        </label>
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => { setInputMethod('screenshot'); clearFiles(); }}
            className={`flex-1 py-2 px-3 rounded-lg transition text-sm ${
              inputMethod === 'screenshot'
                ? 'bg-amber-400 text-slate-900 font-semibold'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Screenshot
          </button>
          <button
            type="button"
            onClick={() => { setInputMethod('pdf'); clearFiles(); }}
            className={`flex-1 py-2 px-3 rounded-lg transition text-sm ${
              inputMethod === 'pdf'
                ? 'bg-amber-400 text-slate-900 font-semibold'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            PDF
          </button>
          <button
            type="button"
            onClick={() => { setInputMethod('text'); clearFiles(); }}
            className={`flex-1 py-2 px-3 rounded-lg transition text-sm ${
              inputMethod === 'text'
                ? 'bg-amber-400 text-slate-900 font-semibold'
                : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}
          >
            Paste Text
          </button>
        </div>

        {inputMethod === 'screenshot' && (
          <div>
            <p className="text-slate-400 text-sm mb-3">
              <strong>Best option!</strong> Upload screenshots of your LinkedIn profile.
              You can add up to 5 images to capture your full profile.
            </p>

            {/* Preview uploaded images */}
            {linkedinFiles.length > 0 && (
              <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {previewUrls.map((url, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={url}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg border border-slate-600"
                    />
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition text-xs font-bold"
                    >
                      ×
                    </button>
                    <span className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1 rounded">
                      {index + 1}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Upload area */}
            {linkedinFiles.length < 5 && (
              <div
                className="border-2 border-dashed border-slate-600 rounded-lg p-4 text-center hover:border-amber-400 transition cursor-pointer"
                onClick={() => document.getElementById('file-upload').click()}
              >
                <input
                  id="file-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                />
                <div className="text-slate-400">
                  <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <p className="font-semibold text-sm">
                    {linkedinFiles.length === 0 ? 'Click to upload screenshots' : 'Add more screenshots'}
                  </p>
                  <p className="text-xs">PNG, JPG • {linkedinFiles.length}/5 uploaded</p>
                </div>
              </div>
            )}

            {linkedinFiles.length > 0 && (
              <p className="text-green-400 text-sm mt-2 text-center font-semibold">
                {linkedinFiles.length} screenshot{linkedinFiles.length > 1 ? 's' : ''} uploaded! Click "Generate My LinkedIn Audit" below to continue.
              </p>
            )}
          </div>
        )}

        {inputMethod === 'pdf' && (
          <div>
            <p className="text-amber-400 text-sm mb-2 font-semibold">
              How to get your LinkedIn PDF:
            </p>
            <p className="text-slate-400 text-sm mb-3">
              Go to your LinkedIn profile - Click "More" button - Select "Save to PDF"
            </p>
            <div
              className="border-2 border-dashed border-slate-600 rounded-lg p-4 text-center hover:border-amber-400 transition cursor-pointer"
              onClick={() => document.getElementById('file-upload').click()}
            >
              <input
                id="file-upload"
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="hidden"
              />
              {linkedinFiles.length > 0 ? (
                <div className="text-green-400">
                  <svg className="w-8 h-8 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="font-semibold text-sm">{linkedinFiles[0].name}</p>
                  <p className="text-xs text-slate-400">Click to change file</p>
                </div>
              ) : (
                <div className="text-slate-400">
                  <svg className="w-10 h-10 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="font-semibold">Click to upload LinkedIn PDF</p>
                </div>
              )}
            </div>
            {linkedinFiles.length > 0 && (
              <p className="text-green-400 text-sm mt-2 text-center font-semibold">
                PDF uploaded! Scroll down and click "Generate My LinkedIn Audit" to continue.
              </p>
            )}
          </div>
        )}

        {inputMethod === 'text' && (
          <div>
            <p className="text-slate-400 text-sm mb-3">
              Copy and paste your LinkedIn headline, about section, and experience.
            </p>
            <textarea
              value={linkedinText}
              onChange={(e) => setLinkedinText(e.target.value)}
              placeholder="Paste your LinkedIn headline, about section, and experience here..."
              rows={8}
              className="w-full px-4 py-3 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-amber-400 transition resize-none"
            />
          </div>
        )}
      </div>

      {/* Submit Button */}
      <button
        type="submit"
        className="w-full py-4 bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900 font-bold text-lg rounded-lg hover:from-amber-500 hover:to-amber-600 transition transform hover:scale-[1.02] active:scale-[0.98]"
      >
        Generate My LinkedIn Audit
      </button>

      <p className="text-center text-slate-500 text-sm">
        Your data is processed securely and never shared
      </p>
    </form>
  );
}

export default InputForm;
